import { useState, useRef } from "react";
import {
  ALL_CUSTOMERS,
  BUTTON_CANCEL,
  BUTTON_DOWNLOAD_TEMPLATE,
  BUTTON_SELECT_CSV_FILE,
  BUTTON_UPLOAD_FILE,
  CART_ABANDONERS,
  CONVERTED_LEADS,
  COUNTRY_CODE_PLACEHOLDER,
  CUSTOMER_TYPES_PLACEHOLDER,
  DESCRIPTION_PLACEHOLDER,
  DISENGAGED_CUSTOMERS,
  EMAIL_PLACEHOLDER,
  FIELD_REQUIRED,
  FIRST_NAME_PLACEHOLDER,
  HIGH_VALUE_CUSTOMERS,
  LAST_NAME_PLACEHOLDER,
  LOYALTY_SIGN_UPS,
  MODAL_UPLOAD_CUSTOM_AUDIENCES_GOOGLE_DESCRIPTION,
  MODAL_UPLOAD_CUSTOM_AUDIENCES_GOOGLE_TITLE,
  NAME_PLACEHOLDER,
  NO_HEADERS_TABLE_VALID_NOT_MODIFY_HEADERS_GOOGLE_CUSTOM_AUDIENCE,
  PAID_SUBSCRIBERS,
  PHONE_PLACEHOLDER,
  POSTAL_CODE_PLACEHOLDER,
  PURCHASERS,
  QUALIFIED_LEADS,
  RETENTION_DAYS_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import ModalBasicLayout from "../../../Modal/ModalBasicLayout";
import { DialogActions, Typography } from "@mui/material";
import ContainerModalActions from "../../../Containers/ContainerModalActions";
import Box from "@mui/material/Box";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { parse } from "papaparse";
import Button from "../../../Buttons/Button";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import AutocompleteField from "../../../Form/AutocompleteField";
import TableContainer from "../../../Tables/TableContainer";
import TableCell from "../../../../pages/BillingAndPayments/components/TableCell";
import { useForm } from "react-hook-form";
import NumberField from "../../../Form/NumberField";
import TextField from "../../../Form/TextField";
import { useDispatchApp } from "../../../../lib/redux";
import { startCreateCustomAudience } from "../../../../actions/adsCampaigns";
import { GOOGLE } from "../../../../utils/constants";
import _ from "lodash";
import { openLink } from "../../../../utils/string";
import CellTypography from "../../../Tables/CellTypography";

const rowsPerPage = 10;

// Normalize header: remove accents, convert to lowercase, remove spaces and special characters
const normalizeHeader = (header) => {
  if (!header) return "";

  return header
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove accents
    .replace(/\s+/g, "") // Remove spaces
    .replace(/[^a-z0-9]/g, ""); // Remove special characters, keep only letters and numbers
};

// Map of normalized header variations to canonical headers
const HEADER_MAPPING = {
  // email variations
  email: "email",
  correo: "email",

  // phone variations
  phone: "phone",
  telefono: "phone",
  celular: "phone",

  // firstName variations
  firstname: "firstName",
  first: "firstName",
  nombre: "firstName",
  nombres: "firstName",

  // lastName variations
  lastname: "lastName",
  last: "lastName",
  apellido: "lastName",
  apellidos: "lastName",

  // countryCode variations
  countrycode: "countryCode",
  country: "countryCode",
  pais: "countryCode",
  codigodepais: "countryCode",
  codigopais: "countryCode",

  // postalCode variations
  postalcode: "postalCode",
  postal: "postalCode",
  zip: "postalCode",
  codigopostal: "postalCode",
};

// Get canonical header from normalized header
const getCanonicalHeader = (header) => {
  const normalized = normalizeHeader(header);
  return HEADER_MAPPING[normalized] || null;
};

// Validate if a row has at least one valid combination:
// 1. email (alone)
// 2. phone (alone)
// 3. firstName + lastName + countryCode + postalCode (all together)
const isValidRow = (row, canonicalHeaders) => {
  // Create an object with row values using canonical headers as keys
  const rowData = {};
  canonicalHeaders.forEach((header, index) => {
    const value = row[index];
    rowData[header] = value !== null && value !== undefined ? value.trim() : "";
  });

  // Helper function to check if a value is not empty
  const hasValue = (key) => {
    const value = rowData[key];
    return value && value !== "" && !_.isEmpty(value);
  };

  // Check combination 1: email alone
  if (hasValue("email")) {
    return true;
  }

  // Check combination 2: phone alone
  if (hasValue("phone")) {
    return true;
  }

  // Check combination 3: firstName + lastName + countryCode + postalCode (all required)
  if (
    hasValue("firstName") &&
    hasValue("lastName") &&
    hasValue("countryCode") &&
    hasValue("postalCode")
  ) {
    return true;
  }

  return false;
};

const ModalUploadCustomAudienceGoogle = ({
  integrationID,
  adAccountID,
  modalOpen,
  onCloseModal = () => {},
  onCallbackSave = () => {},
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const TABLE_TRANSLATIONS = {
    email: t(EMAIL_PLACEHOLDER),
    phone: t(PHONE_PLACEHOLDER),
    firstName: t(FIRST_NAME_PLACEHOLDER),
    lastName: t(LAST_NAME_PLACEHOLDER),
    countryCode: t(COUNTRY_CODE_PLACEHOLDER),
    postalCode: t(POSTAL_CODE_PLACEHOLDER),
  };

  const {
    control,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      customerTypes: [],
      retentionDays: 90,
      dataType: "CONTACT_INFO",
    },
  });

  const onFileChange = (event) => {
    setPage(0);
    setError(null);
    const file = event.target.files[0];
    if (file) {
      parse(file, {
        complete: (results) => {
          if (results.data.length > 0) {
            const rawHeaders = results.data[0].map((header) => header.trim());

            // Map headers to canonical headers and find valid ones
            const headerMapping = rawHeaders
              .map((header, index) => ({
                original: header,
                canonical: getCanonicalHeader(header),
                index,
              }))
              .filter(({ canonical }) => canonical !== null);

            if (headerMapping.length === 0) {
              setError(
                t(
                  NO_HEADERS_TABLE_VALID_NOT_MODIFY_HEADERS_GOOGLE_CUSTOM_AUDIENCE
                )
              );
              setCsvData([]);
              return;
            }

            // Get valid indices and canonical headers
            const validIndices = headerMapping.map(({ index }) => index);
            const canonicalHeaders = headerMapping.map(
              ({ canonical }) => canonical
            );

            // Filter data to keep only valid columns
            const filteredData = results.data
              .slice(1)
              .map((row) => validIndices.map((index) => row[index]));

            // Filter out rows that don't meet validation requirements:
            // Must have at least one of: email, phone, or firstName+lastName+countryCode+postalCode
            const validRows = filteredData.filter((row) =>
              isValidRow(row, canonicalHeaders)
            );

            // Store canonical headers for consistent processing
            setHeaders(canonicalHeaders);
            setCsvData(validRows);
          }
        },
        header: false,
      });
    }
  };

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    const formValues = getValues();

    const customers = csvData.map((row) => {
      const customerData = {};
      headers.forEach((header, colIndex) => {
        customerData[header] = row[colIndex];
      });

      return customerData;
    });

    const customAudienceData = {
      integrationID,
      adAccountID,
      platform: GOOGLE,
      name: formValues.name,
      description: formValues.description,
      retentionDays: formValues.retentionDays,
      type: formValues.dataType,
      customerTypes: formValues.customerTypes || [],
      data: customers,
    };

    setIsLoading(true);
    const response = await dispatch(
      startCreateCustomAudience(customAudienceData)
    );
    setIsLoading(false);

    if (response.ok) {
      onCallbackSave(response.data.customAudience);
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      maxWidth="md"
      scroll="paper"
      title={t(MODAL_UPLOAD_CUSTOM_AUDIENCES_GOOGLE_TITLE)}
      description={t(MODAL_UPLOAD_CUSTOM_AUDIENCES_GOOGLE_DESCRIPTION)}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        {error && (
          <Box
            sx={{
              bgcolor: "error.light",
              p: 2,
              borderRadius: 1,
              whiteSpace: "pre-line",
            }}
          >
            {error}
          </Box>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t(NAME_PLACEHOLDER)}
            name={"name"}
            fullWidth
            variant="filled"
            control={control}
            errors={errors}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
            }}
          />
          <TextField
            label={t(DESCRIPTION_PLACEHOLDER)}
            name={`description`}
            fullWidth
            multiline
            minRows={2}
            maxRows={2}
            variant="filled"
            control={control}
            errors={errors}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
            }}
          />
          <AutocompleteField
            control={control}
            name="customerTypes"
            label={t(CUSTOMER_TYPES_PLACEHOLDER)}
            options={[
              { value: "ALL_CUSTOMERS", label: t(ALL_CUSTOMERS) },
              { value: "PURCHASERS", label: t(PURCHASERS) },
              {
                value: "HIGH_VALUE_CUSTOMERS",
                label: t(HIGH_VALUE_CUSTOMERS),
              },
              {
                value: "DISENGAGED_CUSTOMERS",
                label: t(DISENGAGED_CUSTOMERS),
              },
              { value: "QUALIFIED_LEADS", label: t(QUALIFIED_LEADS) },
              { value: "CONVERTED_LEADS", label: t(CONVERTED_LEADS) },
              { value: "PAID_SUBSCRIBERS", label: t(PAID_SUBSCRIBERS) },
              { value: "LOYALTY_SIGN_UPS", label: t(LOYALTY_SIGN_UPS) },
              { value: "CART_ABANDONERS", label: t(CART_ABANDONERS) },
            ]}
            multiple={true}
            fullWidth={true}
            variant="filled"
            color="primary"
            errors={errors}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
            }}
            sx={{
              ".MuiFormHelperText-root": {
                mx: 0,
              },
            }}
          />
          <TextField
            label={t(RETENTION_DAYS_PLACEHOLDER)}
            name={`retentionDays`}
            fullWidth
            variant="filled"
            InputProps={{
              inputComponent: NumberField,
            }}
            control={control}
            errors={errors}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <input
            type="file"
            accept=".csv"
            onChange={onFileChange}
            style={{ display: "none" }}
            ref={fileInputRef}
          />
          <Button
            type="button"
            color="secondary"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            {t(BUTTON_SELECT_CSV_FILE)}
          </Button>
          <Typography
            variant="caption"
            color="fields.placeholder"
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() =>
              openLink(
                "https://clapp-prod.s3.us-east-1.amazonaws.com/Buzzly/customer_list_google_template.csv"
              )
            }
          >
            {t(BUTTON_DOWNLOAD_TEMPLATE)}
          </Typography>
        </Box>
        {csvData.length > 0 && (
          <TableContainer
            page={page}
            onPageChange={setPage}
            rowsPerPage={rowsPerPage}
            keys={csvData}
            hasMarginBottom={false}
            sx={{
              borderRadius: "0px !important",
            }}
          >
            <TableHead>
              <TableRow>
                {headers.map((header, index) => (
                  <TableCell key={index} isHeader={true}>
                    {TABLE_TRANSLATIONS[header]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {csvData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={cellIndex} indexRow={rowIndex}>
                        <CellTypography>{cell}</CellTypography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </TableContainer>
        )}
      </Box>

      <DialogActions
        sx={{
          p: 0,
        }}
      >
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onCloseModal,
            disabled: isLoading,
          }}
          propsPrimaryButton={{
            showButton: true,
            loading: isLoading,
            disabled: csvData.length === 0,
            text: BUTTON_UPLOAD_FILE,
            onClick: onSubmit,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalUploadCustomAudienceGoogle;
